//deps
import React, { useRef } from "react";
import { isEmpty, useEffectOnce } from "@swiggy-private/core-ui";

//components
import { InlineChip } from "@swiggy-private/dls-web-core/dist/components/Chips/InlineChip";
import { Textarea } from "@swiggy-private/dls-web-core/dist/components/Textarea";
import {
    Container,
    PreviewContainer,
    PreviewContent,
    PreviewImage,
    PredefinedOptionsContainer,
    ButtonContainer,
    InstructionsText,
    StyledButton,
} from "./styles";

//hooks
import { useKeyboardState } from "@swiggy-private/checkout-core";

//types and interfaces
import { BottomInputHalfCardProps } from "./types";
import { TextAreaVariant } from "@swiggy-private/dls-web-core/dist/components/Textarea/types";

const BottomInputHalfCardContent: React.FC<BottomInputHalfCardProps> = ({
    value,
    onChange,
    placeholder,
    maxLength,
    ariaLabel,
    inputTestId,
    previewContent,
    previewImage,
    onPrimaryCtaClick,
    primaryCtaText,
    isPrimaryCtaDisabled,
    primaryCtaTestId,
    onSecondaryCtaClick,
    secondaryCtaText,
    isSecondaryCtaDisabled,
    secondaryCtaTestId,
    predefinedOptions,
    className,
    containerTestId,
    instructions,
    errorText,
    onLimitExceed,
    onImpression,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const hasPreviewContent = !!previewContent || !!previewImage;

    useKeyboardState(containerRef);

    useEffectOnce(() => {
        onImpression?.();
        if (textAreaRef.current) {
            //wait for halfcard slide up animation to complete before focusing
            setTimeout(() => {
                textAreaRef.current?.focus();
            }, 250);
        }
    });

    const handlePredefinedOptionClick = (optionValue: string) => {
        onChange(optionValue);
    };

    return (
        <Container ref={containerRef} className={className} data-testid={containerTestId}>
            {hasPreviewContent && (
                <PreviewContainer>
                    {previewContent && <PreviewContent>{previewContent}</PreviewContent>}
                    {previewImage && <PreviewImage {...previewImage} fit="fill" />}
                </PreviewContainer>
            )}

            <Textarea
                ref={textAreaRef}
                value={value}
                onChange={onChange}
                onLimitExceed={onLimitExceed}
                name=""
                ariaLabel={ariaLabel}
                errorText={errorText}
                inputLimit={maxLength}
                autoResize
                attrs={{
                    "data-testid": inputTestId,
                    placeholder,
                }}
                maxHeight="64px"
                variant={TextAreaVariant.LOW_EMPHASIS}
            />

            {!isEmpty(instructions) && (
                <InstructionsText variant="V2.overline.neutral.medium" color="text_disabled">
                    {instructions}
                </InstructionsText>
            )}

            {!isEmpty(predefinedOptions) && (
                <PredefinedOptionsContainer>
                    {predefinedOptions.map((option, index) => (
                        <InlineChip
                            key={index}
                            label={option.label}
                            showRightIcon={false}
                            unselectedbgColor="background_Secondary"
                            selectedbgColor="background_Secondary"
                            textColor="text_Med_Emphasis"
                            borderRadius={16}
                            onClick={() => handlePredefinedOptionClick(option.value)}
                            selectedborderColor="background_Add_Button"
                            unselectedborderColor="background_Add_Button"
                        />
                    ))}
                </PredefinedOptionsContainer>
            )}

            <ButtonContainer>
                {!isEmpty(secondaryCtaText) && (
                    <StyledButton
                        variant="fullwidth"
                        buttonType="secondary"
                        buttonSize="medium"
                        isDisabled={isSecondaryCtaDisabled}
                        onClick={onSecondaryCtaClick}
                        data-testid={secondaryCtaTestId}
                    >
                        {secondaryCtaText}
                    </StyledButton>
                )}
                <StyledButton
                    variant="fullwidth"
                    buttonType="primary"
                    buttonSize="medium"
                    isDisabled={isPrimaryCtaDisabled}
                    onClick={onPrimaryCtaClick}
                    data-testid={primaryCtaTestId}
                >
                    {primaryCtaText}
                </StyledButton>
            </ButtonContainer>
        </Container>
    );
};

export default React.memo(BottomInputHalfCardContent);
